import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { useMutation } from '@apollo/react-hooks';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';

import './desktop-sign-up-style.scss';
import { useSnackbar } from '@src/providers/SnackbarProvider';
import useIsComDomain from '@src/hooks/useIsComDomain';

import BasicModal from '@src/componentsNew/Modals/BasicModal/BasicModal'; // TODO : remove after com complete
import Button from '@src/componentsNew/Buttons/Button';
import Typography from '@src/componentsNew/Typography/Typography';
import GoogleLoginButton from '@src/componentsNew/Buttons/SocialLoginButton/GoogleLoginButton';
import FacebookLoginButton from '@src/componentsNew/Buttons/SocialLoginButton/FacebookLoginButton';
import VisibilityIcon from '@src/componentsNew/Icons/Icons/VisibilityIcon';
import VisibilityOffIcon from '@src/componentsNew/Icons/Icons/VisibilityOffIcon';
import ArrowBackIcon from '@src/componentsNew/Icons/Icons/ArrowBackIcon';
import useTimer from '@src/hooks/useTimer';

// Query
import { CREATE_OTP, VERIFY_OTP } from '@src/hooks/useOtp/queries';
import { createOtp } from '@organisms/SignupForm/__generated__/createOtp';
import { verifyOtp } from '@organisms/SignupForm/__generated__/verifyOtp';
import { CUSTOMER_SIGNUP } from '@organisms/SignupForm/queries';
import { customerSignUp } from '@organisms/SignupForm/__generated__/customerSignUp';

import { CredentialContext, credentialType } from '@providers';
import { gaV4SignUp } from '@utils';
import { pixelCompleteRegistration } from '@src/utils/pixel';
import {
  ALPHABET_ONLY_REGEX,
  EMAIL_REGEX,
  MOBILE_NUMBER_REGEX,
  OTP_REGEX,
  WRONG_OTP_MESSAGE,
  SIGN_UP_COMPLETE_MESSAGE,
} from '@src/utils/constants';
import { tncUrl, accountLoginUrl } from '@src/app/routes';

interface IProps {
  code?: string;
}

const SignUpFormTitles = {
  '0': 'Sign Up',
  '1': 'OTP Verification',
  '2': 'OTP Verification',
};

const SignUpFormInfoTexts = (step: number, mobileNumber = '', firstName = '') => {
  if (step === 1) {
    return 'OTP verification is required for sign-up';
  } else if (step === 2) {
    return `Enter the OTP you received on\n+91 ${mobileNumber}`;
  }
};

const getButtonTitle = (step: number) => {
  switch (step) {
    case 0:
      return 'Sign Up With Email';
    case 1:
      return 'Request OTP';
    case 2:
      return 'Verify';
    default:
      return '';
  }
};

const DesktopSignUpPage = ({ code }: IProps) => {
  const { showSnackbar } = useSnackbar();
  const isComDomain = useIsComDomain();
  const { anonymousCart, mgsAnonymousCart, createAndSetAnonymousCart, logIn } = useContext<credentialType>(
    CredentialContext,
  );

  const [step, setStep] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const { time, startTimer } = useTimer(60);

  // TODO : remove after com complete
  const [showLoginAlertModal, setShowLoginAlertModal] = useState(false);
  const handleLoginAlertModalClose = (e) => {
    e.stopPropagation();
    setShowLoginAlertModal(false);
  };
  const [showSignUpAlertModal, setShowSignUpAlertModal] = useState(false);
  const handleSignUpAlertModalClose = (e) => {
    e.stopPropagation();
    setShowSignUpAlertModal(false);
  };

  // Mutation
  const [createOtp, { loading: createOtpLoading }] = useMutation<createOtp>(CREATE_OTP);
  const [verifyOtp, { loading: verifyOtpLoading }] = useMutation<verifyOtp>(VERIFY_OTP);
  const [customerSignUpMutation, { loading: customerSignUpLoading }] = useMutation<customerSignUp>(CUSTOMER_SIGNUP);

  // Form validation by step
  const getValidationSchema = (step: number) => {
    switch (step) {
      case 0:
        return Yup.object().shape({
          firstName: Yup.string().required('Enter First name').matches(ALPHABET_ONLY_REGEX, 'Alphabet only'),
          lastName: Yup.string().required('Enter Last name').matches(ALPHABET_ONLY_REGEX, 'Alphabet only'),
          email: Yup.string().required('Enter Email').matches(EMAIL_REGEX, 'Invalid email format'),
          password: Yup.string().required('Enter Password').min(8, 'Password must be at least 8 characters'),
          referralCode: Yup.string().notRequired(),
          tncCheck: Yup.boolean().oneOf([true], 'Please accept'),
        });
      case 1:
        return Yup.object().shape({
          mobileNumber: Yup.string()
            .required('Enter Mobile number')
            .matches(MOBILE_NUMBER_REGEX, 'Please enter a valid mobile number (10 digit)'),
        });
      case 2:
        return Yup.object().shape({
          otp: Yup.string().required('Enter OTP').matches(OTP_REGEX, 'Please enter a valid OTP (6 digit)'),
        });
      default:
        return null;
    }
  };

  const [validationSchema, setValidationSchema] = useState(getValidationSchema(step));

  useEffect(() => {
    setValidationSchema(getValidationSchema(step));
  }, [step]);

  // Form
  const formik = useFormik({
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      mobileNumber: '',
      otp: '',
      referralCode: code || '',
      tncCheck: true,
    },
    validationSchema: validationSchema,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: (values) => {
      // TODO : remove after com complete
      if (isComDomain) {
        setShowSignUpAlertModal(true);
        return;
      }

      if (step === 0) {
        formik.validateForm().then((errors) => {
          if (Object.keys(errors).length === 0) {
            setStep(1);
          }
        });
      } else if (step === 1) {
        formik.validateForm().then((errors) => {
          if (Object.keys(errors).length === 0) {
            createOtp({ variables: { input: { receiver: values.mobileNumber } } }).then((res) => {
              const response = res.data.createOtp;

              if (response.errors.length > 0) {
                showSnackbar({ message: response.errors[0].message });
              } else {
                startTimer();
                setStep(2);
              }
            });
          }
        });
      } else if (step === 2) {
        formik.validateForm().then((errors) => {
          if (Object.keys(errors).length === 0) {
            verifyOtp({ variables: { input: { receiver: values.mobileNumber, value: values.otp } } }).then((res) => {
              const response = res.data.verifyOtp;

              if (response.errors.length > 0) {
                showSnackbar({ message: response.errors[0].message });
                return;
              } else if (!response.verified) {
                showSnackbar({ message: WRONG_OTP_MESSAGE });
                return;
              } else if (response.verified) {
                customerSignUpMutation({
                  variables: {
                    input: {
                      firstName: values.firstName.trim(),
                      lastName: values.lastName.trim(),
                      email: values.email,
                      password: values.password,
                      otpId: response.otp.id,
                      otpValue: values.otp,
                      mobileNumber: values.mobileNumber,
                      referralCode: values.referralCode,
                      cartToken: null,
                    },
                  },
                }).then(async (res) => {
                  const response = res.data.customerSignUp;

                  if (response.errors.length > 0) {
                    showSnackbar({ message: response.errors[0].message });
                    return;
                  } else {
                    showSnackbar({ message: SIGN_UP_COMPLETE_MESSAGE });

                    gaV4SignUp('Email');
                    pixelCompleteRegistration('sign up');

                    // Login
                    if (anonymousCart && mgsAnonymousCart) {
                      logIn({
                        email: values.email,
                        password: values.password,
                      });
                    } else {
                      if (!anonymousCart) {
                        await createAndSetAnonymousCart({ forMgs: false }, false);
                      }
                      if (!mgsAnonymousCart) {
                        await createAndSetAnonymousCart({ forMgs: true }, false);
                      }

                      logIn({
                        email: values.email,
                        password: values.password,
                      });
                    }
                  }
                });
              }
            });
          }
        });
      }
    },
  });

  // Move previous step
  const handleClickBackStepButton = (step: number) => {
    if (step === 1) {
      setStep(0);
    } else if (step === 2) {
      formik.setValues({ ...formik.values, otp: '' });
      setStep(1);
    }
  };

  // OTP 재요청
  const handleClickResendOtpButton = () => {
    createOtp({ variables: { input: { receiver: formik.values.mobileNumber } } }).then((res) => {
      const response = res.data.createOtp;

      if (response.errors.length > 0) {
        showSnackbar({ message: response.errors[0].message });
      } else {
        startTimer();
      }
    });
  };

  // Social Login Button Tnc checking
  const onCheckBeforeSocialLoginButtonClick = () => {
    formik?.validateField('tncCheck');
  };

  return (
    <div id="desktop-signup-wrapper">
      <form className="signup-form" onSubmit={formik?.handleSubmit}>
        <div className="text-wrapper">
          <div className="title-wrapper">
            {step === 1 || step === 2 ? (
              <ArrowBackIcon cName="back-step-btn" onClick={() => handleClickBackStepButton(step)} />
            ) : null}

            <Typography cName="title" variant="h6" component="h4" style={{ color: '#212121' }}>
              {SignUpFormTitles[String(step)]}
            </Typography>
          </div>

          {step !== 0 && (
            <Typography cName="info-text" variant="body3" component="p" style={{ color: '#212121' }}>
              {SignUpFormInfoTexts(step, formik.values.mobileNumber, formik.values.firstName)}
            </Typography>
          )}
        </div>

        {step === 0 && (
          <div className="signup-input-wrapper">
            <div className="name-input-wrapper">
              <TextField
                type="text"
                size="medium"
                className={`form-input first-name ${formik.errors.firstName || formik.errors.lastName ? 'err' : ''}`}
                label="First Name"
                name="firstName"
                value={formik?.values?.firstName}
                onChange={formik?.handleChange}
                error={!!formik.errors.firstName}
                helperText={formik.errors.firstName}
                fullWidth
              />
              <TextField
                type="text"
                size="medium"
                className={`form-input last-name ${formik.errors.firstName || formik.errors.lastName ? 'err' : ''}`}
                label="Last Name"
                name="lastName"
                value={formik?.values?.lastName}
                onChange={formik?.handleChange}
                error={!!formik.errors.lastName}
                helperText={formik.errors.lastName}
                fullWidth
              />
            </div>

            <TextField
              type="email"
              size="medium"
              className="form-input email"
              label="Email"
              name="email"
              value={formik?.values?.email}
              onChange={formik?.handleChange}
              error={!!formik.errors.email}
              helperText={formik.errors.email}
              fullWidth
            />

            <TextField
              type={showPassword ? 'text' : 'password'}
              size="medium"
              className="form-input password"
              label="Password"
              name="password"
              value={formik?.values?.password}
              onChange={formik?.handleChange}
              error={!!formik.errors.password}
              helperText={formik.errors.password}
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword((prev) => !prev)} edge="end">
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              type="text"
              size="medium"
              className="form-input referral-code"
              label="Referral Code (Optional)"
              name="referralCode"
              value={formik?.values?.referralCode}
              onChange={formik?.handleChange}
              fullWidth
            />
          </div>
        )}

        {step === 1 && (
          <div className="signup-input-wrapper">
            <TextField
              type="tel"
              size="medium"
              className="form-input mobile-number"
              label="Mobile number"
              name="mobileNumber"
              value={formik?.values?.mobileNumber}
              onChange={formik?.handleChange}
              error={!!formik.errors.mobileNumber}
              helperText={formik.errors.mobileNumber}
              inputProps={{ maxLength: 10, inputMode: 'numeric', pattern: '[0-9]*' }}
              fullWidth
            />
          </div>
        )}

        {step === 2 && (
          <div className="signup-input-wrapper">
            <TextField
              type="tel"
              size="medium"
              className="form-input otp"
              label="OTP"
              name="otp"
              value={formik?.values?.otp}
              onChange={formik?.handleChange}
              error={!!formik.errors.otp}
              helperText={formik.errors.otp}
              inputProps={{ maxLength: 6, inputMode: 'numeric', pattern: '[0-9]*' }}
              fullWidth
            />

            <Typography cName="timer-text" variant="subtitle1" component="span" style={{ color: '#FF3399' }}>
              {String(time.minutes).padStart(2, '0')}:{String(time.seconds).padStart(2, '0')}
            </Typography>
          </div>
        )}

        <div className="signup-method-button-wrapper">
          {step === 0 && (
            <FormControlLabel
              className="tnc-check"
              control={
                <Checkbox
                  className={`tnc-checkbox ${
                    !!formik.errors.tncCheck && formik?.values?.tncCheck === false ? 'err' : ''
                  }`}
                  name="tncCheck"
                  checked={formik?.values?.tncCheck}
                  onChange={formik?.handleChange}
                />
              }
              label={
                <div className="tnc-check-label-wrapper">
                  <Typography component="span" variant="caption">
                    By checking, I agreed to
                  </Typography>
                  <Link to={tncUrl}>Maccaron’s T&Cs.</Link>
                  <Typography cName="asterisk" component="span" variant="caption">
                    *
                  </Typography>
                </div>
              }
            />
          )}
          {step === 2 && (
            <Button
              type="button"
              size="medium"
              cName="resend-otp-btn"
              variant="text"
              onClick={handleClickResendOtpButton}
            >
              OTP not received
            </Button>
          )}
          <Button
            type="submit"
            size="large"
            cName={`submit-btn ${
              step === 0 &&
              (formik?.values?.firstName === '' ||
                formik?.values?.lastName === '' ||
                formik?.values?.email === '' ||
                formik?.values?.password === '' ||
                !formik?.values?.tncCheck)
                ? 'disable'
                : ''
            } ${step === 1 || step === 2 ? 'no-gradiant' : ''}`}
            variant="contained"
            disabled={createOtpLoading || verifyOtpLoading || customerSignUpLoading}
          >
            {getButtonTitle(step)}
          </Button>

          {step === 0 && !isComDomain && (
            <>
              <div className="divider-wrapper">
                <div className="divider" />
                <Typography variant="body2" component="span" style={{ color: '#666666' }}>
                  or
                </Typography>
                <div className="divider" />
              </div>

              <div className="social-login-button-wrapper">
                <GoogleLoginButton
                  buttonDisabled={formik?.values?.tncCheck === false}
                  onCheckBeforeClick={onCheckBeforeSocialLoginButtonClick}
                />
                <FacebookLoginButton
                  buttonDisabled={formik?.values?.tncCheck === false}
                  onCheckBeforeClick={onCheckBeforeSocialLoginButtonClick}
                />
              </div>
            </>
          )}
        </div>

        {step === 0 && (
          <div className="login-text-wrapper">
            <Typography variant="body2" component="span" style={{ color: '#212121' }}>
              Already have an account?
            </Typography>

            {/* TODO : remove after com complete */}
            {isComDomain ? (
              <Button cName="go-login-btn" variant="text" onClick={() => setShowLoginAlertModal(true)}>
                Log In
              </Button>
            ) : (
              <Link to={accountLoginUrl}>
                <Button size="medium" cName="go-login-btn" variant="text">
                  Log In
                </Button>
              </Link>
            )}
          </div>
        )}
      </form>

      {/* TODO : remove after com complete */}
      {isComDomain && showLoginAlertModal && (
        <BasicModal
          title="Log-in Not Available"
          titleColor={'#212121'}
          content={`Sorry for the inconvenience caused.
          Service is not available currently.
          Thanks for your patience.`}
          contentColor={'#11141D99'}
          open={showLoginAlertModal}
          onClose={handleLoginAlertModalClose}
          closeButtonText="Got it"
          showCloseIcon={false}
        />
      )}
      {isComDomain && showSignUpAlertModal && (
        <BasicModal
          title="Register Not Available"
          titleColor={'#212121'}
          content={`Sorry for the inconvenience caused.
          Service is not available currently.
          Thanks for your patience.`}
          contentColor={'#11141D99'}
          open={showSignUpAlertModal}
          onClose={handleSignUpAlertModalClose}
          closeButtonText="Got it"
          showCloseIcon={false}
        />
      )}
    </div>
  );
};

export default DesktopSignUpPage;
